import { useEffect, useMemo, useState } from "react";
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap } from "rxjs";
import { convertExchangeRate$, type ExchangeRateConvertResponse } from "@apis/http/api";

const DEBOUNCE_MS = 300;

/**
 * 把美元金额换算为用户偏好货币做近似展示。
 * - usdAmount 变化时 300ms 防抖再请求（应对步进器频繁变动）。
 * - 基于 rxjs `switchMap`：频繁输入会自动取消上一笔在途请求，只采用最新结果。
 * - 请求失败时静默降级（返回 null），不阻断支付流程。
 */
export const useExchangeRate = (usdAmount: number) => {
  const [data, setData] = useState<ExchangeRateConvertResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const amount$ = useMemo(() => new Subject<number>(), []);

  useEffect(() => {
    const sub = amount$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        distinctUntilChanged(),
        tap(() => setLoading(true)),
        switchMap((usd) => convertExchangeRate$({ usdAmount: usd }).pipe(catchError(() => of(null)))),
      )
      .subscribe((result) => {
        setData(result ?? null);
        setLoading(false);
      });
    return () => sub.unsubscribe();
  }, [amount$]);

  useEffect(() => {
    if (!usdAmount || usdAmount <= 0) {
      setData(null);
      setLoading(false);
      return;
    }
    amount$.next(usdAmount);
  }, [usdAmount, amount$]);

  return { data, loading };
};
